import React, {  useState } from "react";
import { FiEye, FiEyeOff, FiAlertCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useToaster } from "../../../contexts/ToasterContext";
import { useClientStatus } from "../../../contexts/ClientStatusContext";
import { authApi } from "../../../mocks/auth";
import AuthLayout from '../../../components/Layout/AuthLayout';
import { Input } from '../../../components/ui/input';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const { handleUserLogin } = useClientStatus();

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    // BYPASS AUTH: Navigate to dashboard immediately, no localStorage, no API calls
    showToast("Login successful!", "success");
    handleUserLogin();
    navigate("/dashboard", { replace: true });
    setSubmitting(false);
  };

  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const code = urlParams.get("code");
  //   const state = urlParams.get("state");

  //   if (code && state) {
  //     console.log("both code and state were caught", code);
  //     console.log("both code and state were caught", state);

  //     setLoading(true);
  //     const handleGitHubCallback = async () => {
  //       try {
  //         // Call your backend to exchange code for JWT
  //         const res = await authApi.githubCallback(code, state);
  //         if (res.status === 200) {
  //           const { jwt, user } = res.data;
  //           // Store token in both places for consistency
  //           localStorage.setItem("accessToken", jwt);
  //           localStorage.setItem("jwt", jwt);
  //           localStorage.setItem("userId", user.id);
  //           showToast("GitHub login successful!", "success");
  //           navigate("/dashboard", { replace: true });
  //         }
  //       } catch (error) {
  //         console.error("GitHub callback error:", error);
  //         showToast("GitHub login failed. Please try again.", "error");
  //         // Clean up URL
  //         navigate("/login", { replace: true });
  //       } finally {
  //         setLoading(false);
  //       }
  //     };
  //     handleGitHubCallback();
  //   }
  // }, [navigate, showToast]);

  // const loginWithGithub = async () => {
  //   try {
  //     // Redirect to GitHub OAuth flow - no need to make an API call here
  //     // as the redirect will happen before any response is received
  //     window.location.href = `${ENV.REACT_APP_URL}api/auth/github`;
  //     // The code below will not execute due to the redirect
  //   } catch (error) {
  //     console.log("GitHub login error:", error);
  //     showToast("Failed to initiate GitHub login.", "error");
  //   }
  // };

  // Show loading indicator if code/state or token is present in URL
  // const urlParams = new URLSearchParams(window.location.search);
  // const code = urlParams.get("code");
  // const state = urlParams.get("state");
  // const token = urlParams.get("token");
  // if ((code && state) || token) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-[#F4F0FD] text-black">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C0ACF3] mx-auto mb-4"></div>
  //         <p className="text-[#8D82AA]">Logging in with GitHub</p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <>
      <AuthLayout>
        <div className="space-y-8">
          {/* Logo and Header */}
          <div className="text-center space-y-4">
            <img src="/logo.svg" alt="StackGuard Logo" className="h-[40px] w-[240px] md:h-[50px] md:w-[300px] mx-auto" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Welcome back</h1>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Discover, Remediate and Govern Non-Human Identities (NHIs) across your infrastructure
              </p>
            </div>
          </div>

          <Formik
            initialValues={{ email: "test@admin.com", password: "Test@123!Test@123!Test@123!" }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, values }) => (
              <Form className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <Field name="email">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter email"
                        disabled={isSubmitting}
                        className="w-full h-12 px-4"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="email">
                    {(msg) => (
                      <div className="text-red-400 text-xs flex items-center">
                        <FiAlertCircle className="mr-1" /> {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
                
                {/* Password Field */}
                <div className="space-y-2">
                  <div className="relative">
                    <Field name="password">
                      {({ field }) => (
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          className="w-full h-12 px-4 pr-12"
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-sm text-[#44087D] hover:underline focus:outline-none"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <ErrorMessage name="password">
                    {(msg) => (
                      <div className="text-red-400 text-xs flex items-center">
                        <FiAlertCircle className="mr-1" /> {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>

                {/* General Error */}
                <ErrorMessage name="general">
                  {(msg) => (
                    <div className="text-red-400 text-sm p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
                      <FiAlertCircle className="mr-2" /> {msg}
                    </div>
                  )}
                </ErrorMessage>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`w-full h-12 text-white py-3 rounded-md font-semibold transition-colors ${
                    values.email && values.password 
                      ? 'bg-[#44087D] hover:bg-[#350661]' 
                      : 'bg-[#454545] hover:bg-[#333333]'
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing In...' : 'Sign In'}
                </button>

                {/* Sign Up Link */}
                <div className="text-center">
                  <p className="text-gray-600">
                    New to StackGuard?{' '}
                    <button
                      type="button"
                      onClick={() => navigate("/signup")}
                      className="text-[#44087D] hover:underline font-medium"
                    >
                      Create an account
                    </button>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </AuthLayout>
    </>
  );
};

export default LoginPage;
